import { supabase } from '@/lib/supabase';

export interface FileSequenceInfo {
  id: string;
  filename: string;
  sequence: number[];
  total_trades: number;
  win_trades: number;
  loss_trades: number;
  win_rate: number;
}

export interface BotInfo {
  id: string;
  name: string;
  owner_id: string;
}

export interface BotRealtimeSequence {
  bot_id: string;
  bot_name: string;
  sequence: number[];
  total_trades: number;
  win_trades: number;
  loss_trades: number;
  win_rate: number;
  oldest_trade_timestamp?: string;
  newest_trade_timestamp?: string;
}

export interface DifferenceAnalysis {
  totalDifferences: number;
  differencePercentage: number;
  differences: Array<{
    position: number;
    fileAValue: number;
    fileBBotValue: number;
    context: {
      before: number[];
      after: number[];
    };
  }>;
  consecutiveDifferences: number;
  scatteredDifferences: number;
  lengthDifference: number;
}

export interface CombinedSequence {
  file_a_sequence: number[];
  file_b_sequence: number[];
  realtime_sequence: number[];
  file_b_plus_bot_sequence: number[];
  file_a_stats: {
    total_trades: number;
    win_trades: number;
    loss_trades: number;
    win_rate: number;
  };
  file_b_stats: {
    total_trades: number;
    win_trades: number;
    loss_trades: number;
    win_rate: number;
  };
  realtime_stats: {
    total_trades: number;
    win_trades: number;
    loss_trades: number;
    win_rate: number;
  };
  file_b_plus_bot_stats: {
    total_trades: number;
    win_trades: number;
    loss_trades: number;
    win_rate: number;
  };
}

export interface SequenceComparison {
  file_a_info: FileSequenceInfo;
  file_b_info: FileSequenceInfo;
  bot_info: BotRealtimeSequence;
  combined: CombinedSequence;
  differenceAnalysis: DifferenceAnalysis;
  is_valid: boolean;
  validation_errors: string[];
}

export class SequenceComparisonService {
  /**
   * Get all available trading files for the current user
   */
  static async getTradingFiles(): Promise<{ success: boolean; data?: FileSequenceInfo[]; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('trading_files')
        .select('id, filename, trading_data, total_trades, win_trades, loss_trades')
        .eq('uploaded_by', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const files: FileSequenceInfo[] = (data || []).map(file => {
        const sequence = file.trading_data || [];
        const total_trades = file.total_trades || sequence.length;
        const win_trades = file.win_trades || sequence.filter((x: number) => x === 1).length;
        const loss_trades = total_trades - win_trades;
        const win_rate = total_trades > 0 ? (win_trades / total_trades) * 100 : 0;

        return {
          id: file.id,
          filename: file.filename,
          sequence: sequence,
          total_trades,
          win_trades,
          loss_trades,
          win_rate: Math.round(win_rate * 100) / 100
        };
      });

      return { success: true, data: files };
    } catch (error) {
      console.error('Error fetching trading files:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get all available bots for the current user
   */
  static async getAvailableBots(): Promise<{ success: boolean; data?: BotInfo[]; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('bots')
        .select('id, name, owner_id')
        .eq('owner_id', user.id)
        .order('name', { ascending: true });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching bots:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get bot realtime trading sequence
   */
  static async getBotRealtimeSequence(botId: string): Promise<{ success: boolean; data?: BotRealtimeSequence; error?: string }> {
    try {
      // Get bot info
      const { data: bot, error: botError } = await supabase
        .from('bots')
        .select('id, name')
        .eq('id', botId)
        .single();

      if (botError || !bot) {
        throw new Error('Bot not found');
      }

      // Get realtime trades ordered by timestamp
      const { data: trades, error: tradesError } = await supabase
        .from('bot_realtime_trades')
        .select('win, timestamp')
        .eq('bot_id', botId)
        .order('timestamp', { ascending: true });

      if (tradesError) throw tradesError;

      const sequence = (trades || []).map(trade => trade.win);
      const total_trades = sequence.length;
      const win_trades = sequence.filter(x => x === 1).length;
      const loss_trades = total_trades - win_trades;
      const win_rate = total_trades > 0 ? (win_trades / total_trades) * 100 : 0;

      const result: BotRealtimeSequence = {
        bot_id: bot.id,
        bot_name: bot.name,
        sequence,
        total_trades,
        win_trades,
        loss_trades,
        win_rate: Math.round(win_rate * 100) / 100,
        oldest_trade_timestamp: trades && trades.length > 0 ? trades[0].timestamp : undefined,
        newest_trade_timestamp: trades && trades.length > 0 ? trades[trades.length - 1].timestamp : undefined
      };

      return { success: true, data: result };
    } catch (error) {
      console.error('Error fetching bot realtime sequence:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Compare File A sequence with File B + Bot realtime sequence
   */
  static async compareSequences(fileAId: string, fileBId: string, botId: string): Promise<{ success: boolean; data?: SequenceComparison; error?: string }> {
    try {
      // Get all trading files
      const filesResult = await this.getTradingFiles();
      if (!filesResult.success || !filesResult.data) {
        throw new Error('Failed to fetch trading files');
      }

      // Find File A and File B
      const fileA = filesResult.data.find(f => f.id === fileAId);
      if (!fileA) {
        throw new Error('File A not found');
      }

      const fileB = filesResult.data.find(f => f.id === fileBId);
      if (!fileB) {
        throw new Error('File B not found');
      }

      // Get bot realtime sequence
      const botResult = await this.getBotRealtimeSequence(botId);
      if (!botResult.success || !botResult.data) {
        throw new Error('Failed to fetch bot realtime trades');
      }

      const bot = botResult.data;

      // Combine File B + Bot sequences
      const file_b_plus_bot_sequence = [...fileB.sequence, ...bot.sequence];
      const file_b_plus_bot_total = fileB.total_trades + bot.total_trades;
      const file_b_plus_bot_wins = fileB.win_trades + bot.win_trades;
      const file_b_plus_bot_losses = file_b_plus_bot_total - file_b_plus_bot_wins;
      const file_b_plus_bot_win_rate = file_b_plus_bot_total > 0 ? (file_b_plus_bot_wins / file_b_plus_bot_total) * 100 : 0;

      const combined: CombinedSequence = {
        file_a_sequence: fileA.sequence,
        file_b_sequence: fileB.sequence,
        realtime_sequence: bot.sequence,
        file_b_plus_bot_sequence,
        file_a_stats: {
          total_trades: fileA.total_trades,
          win_trades: fileA.win_trades,
          loss_trades: fileA.loss_trades,
          win_rate: fileA.win_rate
        },
        file_b_stats: {
          total_trades: fileB.total_trades,
          win_trades: fileB.win_trades,
          loss_trades: fileB.loss_trades,
          win_rate: fileB.win_rate
        },
        realtime_stats: {
          total_trades: bot.total_trades,
          win_trades: bot.win_trades,
          loss_trades: bot.loss_trades,
          win_rate: bot.win_rate
        },
        file_b_plus_bot_stats: {
          total_trades: file_b_plus_bot_total,
          win_trades: file_b_plus_bot_wins,
          loss_trades: file_b_plus_bot_losses,
          win_rate: Math.round(file_b_plus_bot_win_rate * 100) / 100
        }
      };

      // Validation
      const validation_errors: string[] = [];
      let is_valid = true;

      if (fileA.sequence.length === 0) {
        validation_errors.push('File A sequence is empty');
        is_valid = false;
      }

      if (fileB.sequence.length === 0) {
        validation_errors.push('File B sequence is empty');
        is_valid = false;
      }

      if (bot.sequence.length === 0) {
        validation_errors.push('Bot has no realtime trades');
        is_valid = false;
      }

      // Check for invalid values in sequences
      const invalidFileAValues = fileA.sequence.filter(x => x !== 0 && x !== 1);
      if (invalidFileAValues.length > 0) {
        validation_errors.push(`File A sequence contains invalid values: ${invalidFileAValues.join(', ')}`);
        is_valid = false;
      }

      const invalidFileBValues = fileB.sequence.filter(x => x !== 0 && x !== 1);
      if (invalidFileBValues.length > 0) {
        validation_errors.push(`File B sequence contains invalid values: ${invalidFileBValues.join(', ')}`);
        is_valid = false;
      }

      const invalidBotValues = bot.sequence.filter(x => x !== 0 && x !== 1);
      if (invalidBotValues.length > 0) {
        validation_errors.push(`Bot sequence contains invalid values: ${invalidBotValues.join(', ')}`);
        is_valid = false;
      }

      // Calculate difference analysis
      const differenceAnalysis = this.calculateDifferenceAnalysis(
        fileA.sequence,
        file_b_plus_bot_sequence
      );

      const result: SequenceComparison = {
        file_a_info: fileA,
        file_b_info: fileB,
        bot_info: bot,
        combined,
        differenceAnalysis,
        is_valid,
        validation_errors
      };

      return { success: true, data: result };
    } catch (error) {
      console.error('Error comparing sequences:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Calculate difference analysis between two sequences
   */
  private static calculateDifferenceAnalysis(sequenceA: number[], sequenceB: number[]): DifferenceAnalysis {
    const differences: DifferenceAnalysis['differences'] = [];
    const minLength = Math.min(sequenceA.length, sequenceB.length);
    const lengthDifference = Math.abs(sequenceA.length - sequenceB.length);
    
    // Find position-by-position differences
    for (let i = 0; i < minLength; i++) {
      if (sequenceA[i] !== sequenceB[i]) {
        const contextBefore = [];
        const contextAfter = [];
        
        // Get context (2 positions before and after)
        for (let j = Math.max(0, i - 2); j < i; j++) {
          contextBefore.push(sequenceA[j]);
        }
        for (let j = i + 1; j < Math.min(sequenceA.length, i + 3); j++) {
          contextAfter.push(sequenceA[j]);
        }
        
        differences.push({
          position: i,
          fileAValue: sequenceA[i],
          fileBBotValue: sequenceB[i],
          context: {
            before: contextBefore,
            after: contextAfter
          }
        });
      }
    }
    
    // Calculate consecutive vs scattered differences
    let consecutiveDifferences = 0;
    let scatteredDifferences = 0;
    
    for (let i = 0; i < differences.length; i++) {
      const isConsecutive = i > 0 && differences[i].position === differences[i - 1].position + 1;
      const isNextConsecutive = i < differences.length - 1 && differences[i + 1].position === differences[i].position + 1;
      
      if (isConsecutive || isNextConsecutive) {
        consecutiveDifferences++;
      } else {
        scatteredDifferences++;
      }
    }
    
    const totalDifferences = differences.length + lengthDifference;
    const differencePercentage = Math.max(sequenceA.length, sequenceB.length) > 0 
      ? (totalDifferences / Math.max(sequenceA.length, sequenceB.length)) * 100 
      : 0;
    
    return {
      totalDifferences,
      differencePercentage: Math.round(differencePercentage * 100) / 100,
      differences,
      consecutiveDifferences,
      scatteredDifferences,
      lengthDifference
    };
  }

  /**
   * Export comparison data to CSV
   */
  static exportToCSV(comparison: SequenceComparison): string {
    const headers = ['Position', 'File_A_Value', 'File_B_Value', 'Bot_Value', 'File_B_Plus_Bot_Value', 'Difference'];
    const rows: string[][] = [headers];

    // Add sequence data with difference indicators
    const maxLength = Math.max(
      comparison.combined.file_a_sequence.length,
      comparison.combined.file_b_plus_bot_sequence.length
    );

    for (let i = 0; i < maxLength; i++) {
      const fileAValue = i < comparison.combined.file_a_sequence.length ? comparison.combined.file_a_sequence[i] : '';
      const fileBValue = i < comparison.combined.file_b_sequence.length ? comparison.combined.file_b_sequence[i] : '';
      const botValue = i < comparison.combined.realtime_sequence.length ? comparison.combined.realtime_sequence[i] : '';
      const combinedValue = i < comparison.combined.file_b_plus_bot_sequence.length ? comparison.combined.file_b_plus_bot_sequence[i] : '';
      
      // Check if this position has a difference
      const hasDifference = comparison.differenceAnalysis.differences.some(diff => diff.position === i);
      const differenceMarker = hasDifference ? 'DIFF' : 'MATCH';
      
      rows.push([
        i.toString(),
        fileAValue.toString(),
        fileBValue.toString(),
        botValue.toString(),
        combinedValue.toString(),
        differenceMarker
      ]);
    }

    // Add difference analysis summary
    rows.push([]);
    rows.push(['=== DIFFERENCE ANALYSIS ===']);
    rows.push(['Total Differences', comparison.differenceAnalysis.totalDifferences.toString()]);
    rows.push(['Difference Percentage', `${comparison.differenceAnalysis.differencePercentage}%`]);
    rows.push(['Consecutive Differences', comparison.differenceAnalysis.consecutiveDifferences.toString()]);
    rows.push(['Scattered Differences', comparison.differenceAnalysis.scatteredDifferences.toString()]);
    rows.push(['Length Difference', comparison.differenceAnalysis.lengthDifference.toString()]);

    return rows.map(row => row.join(',')).join('\n');
  }
}